import {
  type GameSubstitutionRequestBody,
  postGameByGameIdSubstituteMutation,
  TeamSide,
} from "@aura-frontend/omnibus-az-client";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Select,
  toast,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  resetPitchCount,
  setCurrentCatcher,
  setCurrentPitcher,
} from "../../store/slices/game";
import {
  setFocusedEventPayload,
  setTrackingState,
} from "../../store/slices/socket";
import { selectFocusedEvent } from "../../store/slices/socket/selectors";
import { setModalState } from "../../store/slices/view";
import type { RootState } from "../../store/store";
import type { IPlayer } from "../../store/types";
import { mapIPlayerToLineupEntry } from "../../utils/api-helpers";
import { handleErrorNotification } from "../../utils/handleErrorNotification";
import { useFetchSavedLineupEntry } from "../../utils/savedLineupEntry";
import { sortAlphabetic } from "../../utils/sorters";
import { useUpdateGameState } from "../../utils/useUpdateGameState";

export const DefensiveSubModal = () => {
  const dispatch = useDispatch();
  const updateGameState = useUpdateGameState();
  const fetchSavedLineupEntry = useFetchSavedLineupEntry();
  const { state } = useSelector((state: RootState) => state.view.modalState);
  const { away, home } = useSelector((state: RootState) => state.game.rosters);
  const sessionUuid = useSelector((state: RootState) => state.session.uuid);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const inningHalf = gameState.inning.half;
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);

  const defenseTeam = inningHalf === "top" ? home : away;
  const teamSide: TeamSide = inningHalf === "top" ? "home" : "away";
  const [replacementPlayerId, setReplacementPlayerId] = useState("");
  const subType = state === "Pitcher Sub" ? "pitcher" : "catcher";
  const isOpen = state === "Pitcher Sub" || state === "Catcher Sub";

  const replacementPlayer = defenseTeam.roster.find(
    (p) => p.uuid === replacementPlayerId,
  );

  const rosterOptions = useMemo(
    () =>
      sortAlphabetic(defenseTeam.roster, "lastName").map((player) => ({
        value: player.uuid,
        label: `${player.lastName}, ${player.firstName}`,
      })),
    [defenseTeam.roster],
  );

  const { mutate: substituteMutation, isPending: isLoadingSubstitute } =
    useMutation({
      mutationFn: postGameByGameIdSubstituteMutation().mutationFn,
      onError: (error: Error) => {
        const currentPlayer =
          subType === "pitcher"
            ? defenseTeam.currentPitcher
            : defenseTeam.currentCatcher;

        const matchingLineupPlayers = defenseTeam.currentLineup.filter(
          (p) => p.uuid === currentPlayer?.uuid,
        );

        Sentry.captureException(error, {
          contexts: {
            substitution: {
              teamId: defenseTeam.uuid,
              team: teamSide,
              subType,
              replacementPlayerId,
              replacementPlayerName: replacementPlayer
                ? `${replacementPlayer.lastName}, ${replacementPlayer.firstName}`
                : "Unknown",
              currentPlayerValid: isPlayerValid(currentPlayer),
              currentPlayerId: currentPlayer?.uuid || "undefined",
              currentPlayerName: isPlayerValid(currentPlayer)
                ? `${currentPlayer!.firstName} ${currentPlayer!.lastName}`
                : "invalid/undefined",
              // Full currentPlayer object with all fields
              currentPlayer: currentPlayer
                ? {
                    uuid: currentPlayer.uuid,
                    firstName: currentPlayer.firstName,
                    lastName: currentPlayer.lastName,
                    batHand: currentPlayer.batHand,
                    throwHand: currentPlayer.throwHand,
                    position: currentPlayer.position,
                  }
                : null,
              // Full lineup with details instead of just names
              lineup: defenseTeam.currentLineup.map((p) => ({
                uuid: p.uuid,
                name: `${p.lastName}, ${p.firstName}`,
                position: p.position,
                batHand: p.batHand,
                throwHand: p.throwHand,
              })),
              // How many times does current player appear in lineup?
              playerOccurrencesInLineup: matchingLineupPlayers.length,
              // Show all matching lineup entries
              matchingLineupEntries: matchingLineupPlayers.map((p) => ({
                position: p.position,
                name: `${p.lastName}, ${p.firstName}`,
                batHand: p.batHand,
                throwHand: p.throwHand,
              })),
              // What position was sent in the request?
              positionSentInRequest: subType,
              // What positions exist in lineup for this player?
              positionsInLineup: matchingLineupPlayers.map((p) => p.position),
            },
          },
        });

        handleErrorNotification(
          "An error occurred while trying to substitute the player",
          error,
        );
      },
    });

  const handleClose = () => {
    dispatch(setModalState({ state: null, data: {} }));
    dispatch(setTrackingState(true));
    setReplacementPlayerId("");
  };

  const isPlayerValid = (
    player: IPlayer | null | undefined,
  ): player is IPlayer => {
    return !!(player?.uuid && player?.lastName && player?.firstName);
  };

  const buildSubBody = async (
    inPlayer: IPlayer,
    outPlayer: IPlayer | null | undefined,
    position: "pitcher" | "catcher",
  ): Promise<GameSubstitutionRequestBody> => ({
    in: mapIPlayerToLineupEntry(inPlayer, position),
    ...(isPlayerValid(outPlayer) && {
      out: await fetchSavedLineupEntry(
        teamSide,
        mapIPlayerToLineupEntry(outPlayer, position),
      ),
    }),
    side: teamSide,
  });

  const handleSubFocusPlayer = async () => {
    if (!replacementPlayer) return;

    const currentPlayer =
      subType === "pitcher"
        ? defenseTeam.currentPitcher
        : defenseTeam.currentCatcher;
    const body = await buildSubBody(replacementPlayer, currentPlayer, subType);

    substituteMutation(
      {
        body,
        path: { game_id: sessionUuid },
      },
      {
        onSuccess: (data) => {
          const { gameState: gs, game } = data;
          updateGameState(gs, game, true);
          if (subType === "pitcher") {
            dispatch(
              setCurrentPitcher({ team: teamSide, player: replacementPlayer }),
            );
            dispatch(resetPitchCount({ team: teamSide }));
          } else {
            dispatch(
              setCurrentCatcher({ team: teamSide, player: replacementPlayer }),
            );
          }
          dispatch(
            setFocusedEventPayload({
              ...focusedEvent,
              focusedPlayers: {
                ...focusedEvent.focusedPlayers,
                ...(subType === "pitcher"
                  ? { focusedPitcher: replacementPlayer }
                  : { focusedCatcher: replacementPlayer }),
              },
            }),
          );
          handleClose();
        },
      },
    );
  };

  const handleSubCurrentPlayer = async () => {
    if (!replacementPlayer) return;

    if (subType === "pitcher") {
      if (replacementPlayer.uuid === defenseTeam.currentPitcher.uuid) {
        toast(
          `${defenseTeam.currentPitcher.firstName} ${defenseTeam.currentPitcher.lastName} is already the current pitcher!`,
          { variant: "info" },
        );
        return;
      }

      const currentPitcher = defenseTeam.currentPitcher;
      const body = await buildSubBody(
        replacementPlayer,
        currentPitcher,
        "pitcher",
      );

      substituteMutation(
        {
          body,
          path: { game_id: sessionUuid },
        },
        {
          onSuccess: (data) => {
            const { gameState: gs, game } = data;
            updateGameState(gs, game, true);
            dispatch(
              setCurrentPitcher({ team: teamSide, player: replacementPlayer }),
            );
            dispatch(resetPitchCount({ team: teamSide }));
            toast(
              `${replacementPlayer.firstName} ${replacementPlayer.lastName} subbed in for ${currentPitcher.firstName} ${currentPitcher.lastName}`,
              { variant: "success" },
            );
            handleClose();
          },
        },
      );
    } else {
      if (replacementPlayer.uuid === defenseTeam.currentCatcher.uuid) {
        toast(
          `${defenseTeam.currentCatcher.firstName} ${defenseTeam.currentCatcher.lastName} is already the current catcher!`,
          { variant: "info" },
        );
        return;
      }

      const currentCatcher = defenseTeam.currentCatcher;
      const body = await buildSubBody(
        replacementPlayer,
        currentCatcher,
        "catcher",
      );

      substituteMutation(
        {
          body,
          path: { game_id: sessionUuid },
        },
        {
          onSuccess: (data) => {
            const { gameState: gs, game } = data;
            updateGameState(gs, game, true);
            dispatch(
              setCurrentCatcher({ team: teamSide, player: replacementPlayer }),
            );
            toast(
              `${replacementPlayer.firstName} ${replacementPlayer.lastName} subbed in for ${currentCatcher.firstName || "Empty Catcher Slot"} ${currentCatcher.lastName || ""}`,
              { variant: "success" },
            );
            handleClose();
          },
        },
      );
    }
  };

  const handleSubmit = () => {
    if (isViewingPreviousEvent) {
      handleSubFocusPlayer();
    } else {
      handleSubCurrentPlayer();
    }
  };

  const currentPlayerName =
    subType === "pitcher"
      ? `${defenseTeam.currentPitcher.firstName} ${defenseTeam.currentPitcher.lastName}`
      : `${defenseTeam.currentCatcher.firstName} ${defenseTeam.currentCatcher.lastName}`;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>
                Change {subType === "pitcher" ? "Pitcher" : "Catcher"}
              </DialogTitle>
            </DialogHeader>

            {isViewingPreviousEvent ? (
              <p className="text-sm font-medium">Select player for event</p>
            ) : (
              <p className="text-sm font-medium">
                Sub out <span className="text-blue">{currentPlayerName}</span>{" "}
                for:
              </p>
            )}
            <div className="mt-4">
              <Select
                value={replacementPlayerId || undefined}
                onValueChange={(value) => setReplacementPlayerId(String(value))}
                placeholder="Select a player..."
                items={rosterOptions}
              />
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleSubmit}
                isDisabled={!replacementPlayer || isLoadingSubstitute}
                isLoading={isLoadingSubstitute}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
